import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnalyticsApiService from '../services/analyticsApi';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

interface FlagKind {
  flag_kind_id: number;
  name: string;
  description?: string;
}

interface UserFlag {
  flag_kind_id: number;
  flag_name: string;
  flag_description?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

interface FlagModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  userName: string;
  conversationId?: number;
}

interface BugMetadata {
  BugDate: string;
  BugDescription: string;
  BugAuthor: string;
  BugReportedToWho: string;
}

interface TypeBMetadata {
  Reason: string;
  Author: string;
}

export function FlagModal({ isOpen, onClose, userId, userName, conversationId }: FlagModalProps) {
  const { user } = useAuth();
  const [flagKinds, setFlagKinds] = useState<FlagKind[]>([]);
  const [userFlags, setUserFlags] = useState<UserFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingFlags, setSavingFlags] = useState<Set<number>>(new Set());
  
  // State for active metadata forms
  const [activeMetadataForm, setActiveMetadataForm] = useState<number | null>(null);
  const [bugMetadata, setBugMetadata] = useState<BugMetadata>({
    BugDate: new Date().toISOString().split('T')[0], // Today's date
    BugDescription: '',
    BugAuthor: user?.username || '',
    BugReportedToWho: ''
  });
  const [typeBMetadata, setTypeBMetadata] = useState<TypeBMetadata>({
    Reason: '',
    Author: user?.username || ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
      // Reset metadata forms
      setBugMetadata({
        BugDate: new Date().toISOString().split('T')[0],
        BugDescription: '',
        BugAuthor: user?.username || '',
        BugReportedToWho: ''
      });
      setTypeBMetadata({
        Reason: '',
        Author: user?.username || ''
      });
      setActiveMetadataForm(null);
    }
  }, [isOpen, userId, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [kindsResponse, userFlagsResponse] = await Promise.all([
        AnalyticsApiService.getFlagKinds(),
        AnalyticsApiService.getUserFlags(userId)
      ]);
      
      setFlagKinds(kindsResponse.flag_kinds);
      setUserFlags(userFlagsResponse.flags);
    } catch (error) {
      console.error('Error loading flag data:', error);
      toast.error('Failed to load flag data');
    } finally {
      setLoading(false);
    }
  };

  const needsMetadata = (flagName: string): boolean => {
    return flagName === 'Bug' || flagName === 'Type B';
  };

  const handleToggleFlag = async (flagKindId: number, flagName: string) => {
    const isCurrentlyFlagged = userFlags.some(f => f.flag_kind_id === flagKindId);
    
    // If removing flag, just remove it
    if (isCurrentlyFlagged) {
      setSavingFlags(prev => new Set(prev).add(flagKindId));
      try {
        await AnalyticsApiService.removeFlag(userId, flagKindId);
        setUserFlags(prev => prev.filter(f => f.flag_kind_id !== flagKindId));
        toast.success(`Removed "${flagName}" flag from ${userName}`);
      } catch (error: any) {
        console.error('Error removing flag:', error);
        toast.error(error.message || 'Failed to remove flag');
      } finally {
        setSavingFlags(prev => {
          const next = new Set(prev);
          next.delete(flagKindId);
          return next;
        });
      }
      return;
    }

    // If adding flag and it needs metadata, show the form
    if (needsMetadata(flagName)) {
      setActiveMetadataForm(flagKindId);
      return;
    }

    // Otherwise, add flag without metadata
    await addFlagWithMetadata(flagKindId, flagName, undefined);
  };

  const handleAddMoreMetadata = (flagKindId: number) => {
    setActiveMetadataForm(flagKindId);
  };

  const addFlagWithMetadata = async (flagKindId: number, flagName: string, metadata?: Record<string, any>) => {
    setSavingFlags(prev => new Set(prev).add(flagKindId));
    
    try {
      // Always include conversation_id in metadata when available
      const payloadMetadata = (() => {
        const base = metadata ? { ...metadata } : {} as Record<string, any>;
        if (conversationId != null && base.conversation_id == null) {
          base.conversation_id = conversationId;
        }
        return Object.keys(base).length > 0 ? base : undefined;
      })();

      const response = await AnalyticsApiService.addFlag(userId, flagKindId, payloadMetadata);
      await loadData(); // Reload to get the full flag details
      
      // Use the message from the API response if available
      const message = response?.data?.flag?.message || `Added "${flagName}" flag to ${userName}`;
      
      // Show different messages based on whether metadata was appended
      if (message.includes('appended') || message.includes('updated')) {
        toast.success(`📝 Updated "${flagName}" flag metadata for ${userName}`);
      } else if (message.includes('already exists')) {
        toast.info(`"${flagName}" flag already applied to ${userName}`);
      } else {
        toast.success(`✅ Added "${flagName}" flag to ${userName}`);
      }
      
      setActiveMetadataForm(null);
    } catch (error: any) {
      console.error('Error adding flag:', error);
      toast.error(error.message || 'Failed to add flag');
    } finally {
      setSavingFlags(prev => {
        const next = new Set(prev);
        next.delete(flagKindId);
        return next;
      });
    }
  };

  const handleSubmitBugMetadata = (flagKindId: number, flagName: string) => {
    // Validate required fields
    if (!bugMetadata.BugDescription.trim()) {
      toast.error('Bug description is required');
      return;
    }
    if (!bugMetadata.BugReportedToWho.trim()) {
      toast.error('Bug reported to whom is required');
      return;
    }

    addFlagWithMetadata(flagKindId, flagName, bugMetadata);
  };

  const handleSubmitTypeBMetadata = (flagKindId: number, flagName: string) => {
    // Validate required fields
    if (!typeBMetadata.Reason.trim()) {
      toast.error('Reason for Type B is required');
      return;
    }

    addFlagWithMetadata(flagKindId, flagName, typeBMetadata);
  };

  const renderMetadataForm = (flagKind: FlagKind) => {
    if (activeMetadataForm !== flagKind.flag_kind_id) return null;

    if (flagKind.name === 'Bug') {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bug Date
            </label>
            <input
              type="date"
              value={bugMetadata.BugDate}
              onChange={(e) => setBugMetadata(prev => ({ ...prev, BugDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bug Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={bugMetadata.BugDescription}
              onChange={(e) => setBugMetadata(prev => ({ ...prev, BugDescription: e.target.value }))}
              placeholder="Describe the bug in natural language..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bug Author
            </label>
            <input
              type="text"
              value={bugMetadata.BugAuthor}
              onChange={(e) => setBugMetadata(prev => ({ ...prev, BugAuthor: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bug Reported To <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bugMetadata.BugReportedToWho}
              onChange={(e) => setBugMetadata(prev => ({ ...prev, BugReportedToWho: e.target.value }))}
              placeholder="Who was this bug reported to?"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => handleSubmitBugMetadata(flagKind.flag_kind_id, flagKind.name)}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Submit Bug Flag
            </button>
            <button
              onClick={() => setActiveMetadataForm(null)}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      );
    }

    if (flagKind.name === 'Type B') {
      return (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Type B <span className="text-red-500">*</span>
            </label>
            <textarea
              value={typeBMetadata.Reason}
              onChange={(e) => setTypeBMetadata(prev => ({ ...prev, Reason: e.target.value }))}
              placeholder="Explain why this user is flagged as Type B..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Author
            </label>
            <input
              type="text"
              value={typeBMetadata.Author}
              onChange={(e) => setTypeBMetadata(prev => ({ ...prev, Author: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => handleSubmitTypeBMetadata(flagKind.flag_kind_id, flagKind.name)}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Submit Type B Flag
            </button>
            <button
              onClick={() => setActiveMetadataForm(null)}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      );
    }

    return null;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', duration: 0.3 }}
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                🏴 Flag User
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {userName} (User ID: {userId})
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-gray-600 dark:border-t-gray-300 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {flagKinds.map(flagKind => {
                  const isFlagged = userFlags.some(f => f.flag_kind_id === flagKind.flag_kind_id);
                  const isSaving = savingFlags.has(flagKind.flag_kind_id);
                  const showingForm = activeMetadataForm === flagKind.flag_kind_id;
                  const userFlag = userFlags.find(f => f.flag_kind_id === flagKind.flag_kind_id);

                  return (
                    <div key={flagKind.flag_kind_id} className="border-2 rounded-xl overflow-hidden transition-all"
                      style={{
                        borderColor: isFlagged 
                          ? 'rgb(59, 130, 246)' 
                          : showingForm 
                          ? 'rgb(156, 163, 175)' 
                          : 'rgb(229, 231, 235)'
                      }}
                    >
                      <label
                        className={`
                          flex items-start p-4 cursor-pointer transition-all
                          ${isFlagged 
                            ? 'bg-blue-50 dark:bg-blue-950/30' 
                            : showingForm
                            ? 'bg-gray-50 dark:bg-gray-900'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-900'
                          }
                          ${isSaving ? 'opacity-50 cursor-wait' : ''}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isFlagged}
                          onChange={() => handleToggleFlag(flagKind.flag_kind_id, flagKind.name)}
                          disabled={isSaving || showingForm}
                          className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {flagKind.name}
                          </div>
                          {flagKind.description && (
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                              {flagKind.description}
                            </div>
                          )}
                          {isFlagged && userFlag?.metadata && (
                            <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold text-gray-700 dark:text-gray-300">
                                  Metadata History ({Array.isArray(userFlag.metadata) ? userFlag.metadata.length : 1} entries):
                                </div>
                                {needsMetadata(flagKind.name) && !showingForm && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleAddMoreMetadata(flagKind.flag_kind_id);
                                    }}
                                    className="px-2 py-1 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                  >
                                    + Add More
                                  </button>
                                )}
                              </div>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {(Array.isArray(userFlag.metadata) ? userFlag.metadata : [userFlag.metadata]).map((entry: any, idx: number) => (
                                  <div key={idx} className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                                    {entry.timestamp && (
                                      <div className="text-gray-500 dark:text-gray-400 mb-1 text-[10px]">
                                        {new Date(entry.timestamp).toLocaleString()}
                                      </div>
                                    )}
                                    <div className="space-y-1 text-gray-700 dark:text-gray-300">
                                      {Object.entries(entry).filter(([key]) => key !== 'timestamp').map(([key, value]) => (
                                        <div key={key}>
                                          <span className="font-medium">{key}:</span>{' '}
                                          <span className="text-gray-600 dark:text-gray-400">{String(value)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {isSaving && (
                          <div className="ml-2">
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
                          </div>
                        )}
                      </label>
                      
                      {renderMetadataForm(flagKind)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {userFlags.length} flag{userFlags.length !== 1 ? 's' : ''} applied
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
